import { Component, computed, effect, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';
import { Impediment, ImpedimentGroup } from '../../../api/app-types/impediment.app';
import { IMPEDIMENT_PROVIDER } from '../../../api/services/impediment.service';
import { LvrClientReport } from '../../../core/components/client-report/client-report';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrHtmlEditor } from '../../../core/components/html-editor/html-editor';
import { mergeOriginalTemplateWithEditedContent } from '../../../core/components/html-editor/template.func';
import { ImpedimentList } from '../../../core/components/impediment-list/impediment-list';
import { hasError } from '../../../shared/helpers/has-error.func';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { validateIfIndicatorsAreSet } from '../../../shared/validators/editor-report-indicators.validator';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-impediment-report',
	templateUrl: './impediment-report.html',
	styleUrl: './impediment-report.scss',
	imports: [
		TranslocoPipe,
		LvrHeading,
		LvrHtmlEditor,
		ReactiveFormsModule,
		ImpedimentList,
		MatTooltip,
		LvrDefaultActions,
		LvrButton,
	],
})
export class LvrImpedimentReport extends LvrClientReport {
	public readonly hasError = hasError;
	public readonly availableImpediments = this.resolveDataAsSignal<ImpedimentGroup[]>('impedimentGroups');
	public readonly dueDate = this.resolveParamAsSignal<string | undefined>('dueDate');
	private readonly fb = inject(FormBuilder);
	private readonly impedimentApi = inject(IMPEDIMENT_PROVIDER);
	private readonly impedimentsOnReportPage =
		this.resolveDataAsSignal<PaginatedResponse<Impediment>>('impedimentsOnReportPage');
	public readonly isUsbAssignment = computed(() => this.assignment()?.typeOfCare?.initial === 'ÜSB');

	public readonly isDraft = computed(() => {
		const report = this.report();
		const template = report?.template;

		if (!report || !template) return true; // fallback

		const content = mergeOriginalTemplateWithEditedContent(template.htmlContent, this.updateHtmlContent());

		return !validateIfIndicatorsAreSet(content);
	});

	public readonly endTooltip = computed(() => {
		if (!this.report()?.canBeSubmitted || this.isDraft()) {
			return 'assignment.report.end_disabled';
		}
		return '';
	});

	public readonly form = this.fb.group({
		impediments: this.fb.control<number[] | null>(null),
	});

	private readonly IMPEDIMENT_REPORT_SAVE_LOADING_KEY = `impediment-report-${this.assignmentId()}.save`;
	public readonly isLoadingForSaveImpedimentReport = this.loading.isCurrentlyLoading(
		this.IMPEDIMENT_REPORT_SAVE_LOADING_KEY,
		'requestKey',
	);

	public constructor() {
		super();
		effect(() => {
			if (!this.impedimentsOnReportPage() || this.impedimentsOnReportPage()!.entities.length < 1) return;
			const impedimentIds = this.impedimentsOnReportPage()!.entities.map((i) => i.id);
			this.form.get('impediments')!.patchValue(impedimentIds);
		});
	}

	public async save() {
		if (!this.canEditReport()) return;

		const report = this.report()!;
		const template = report.template;

		if (!template) return;

		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), this.IMPEDIMENT_REPORT_SAVE_LOADING_KEY);

		try {
			const content = mergeOriginalTemplateWithEditedContent(template.htmlContent, this.updateHtmlContent());

			const impedimentIds = this.form.value.impediments || [];
			await this.impedimentApi.saveImpedimentsToReport(report.id, impedimentIds);
			await this.api.updateReportDocument({
				assignmentId: this.assignmentId()!,
				htmlContent: content,
				reportId: report.id,
				isFinal: false,
				isDraft: !validateIfIndicatorsAreSet(content),
				reportDocumentTemplateId: template.id,
			});

			await this.navigateAfterFinished(this.getAssignmentDetailsCommands('../../..'), {
				relativeTo: this.route,
			});

			this.ntfy.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.save_report_success')),
			);
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.save_report_error')),
			);
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	public async finalize() {
		if (!this.canFinalizeReport()) return;
		await this.finalizeReport('../../..');
	}
}
